import { AuditLogInterface } from "../types/AuditLogInterface";
import {
  StageInterface,
  LotForReportsInterface,
} from "../types/StageInterface";
import { LotStatus } from "@/enums/LotStatus";
import { formatPrice } from "@/helpers/transformation/price";

interface ActivityBreakdownItem {
  lotTitle: string;
  lotId: string;
  date: Date;
  price: number;
  buyer: string;
  badge?: string;
  badgeClass?: string;
}

interface ActivityCounts {
  putOnHold: number;
  contractsSigned: number;
  cancellations: number;
  unconditional: number;
  settlements: number;
}

interface ActivityDetails {
  settlements: ActivityBreakdownItem[];
  contractsSigned: ActivityBreakdownItem[];
  contractsTerminated: ActivityBreakdownItem[];
  putOnHold: ActivityBreakdownItem[];
  holdsProgressed: ActivityBreakdownItem[];
  holdsCancelled: ActivityBreakdownItem[];
  wentUnconditional: ActivityBreakdownItem[];
}

interface LotStatusInfo {
  statusState:
    | "still-hold"
    | "still-under-contract"
    | "still-unconditional"
    | "cancelled"
    | "progressed";
  badge: string;
  badgeClass: string;
  buyerFromCancellation?: string;
}

export interface SalesActivityResult {
  counts: ActivityCounts;
  details: ActivityDetails;
  notes: {
    putOnHold: string;
    settlements: string;
  };
}

const getLotFromStages = (
  stages: StageInterface[],
  lotId: string
): LotForReportsInterface | null => {
  for (const stage of stages) {
    const lot = stage.lots.find((l) => l._id === lotId);

    if (lot) return lot;
  }

  return null;
};

const getBuyerName = (firstBuyer?: string, secondBuyer?: string): string => {
  if (firstBuyer && secondBuyer) {
    return `${firstBuyer} & ${secondBuyer}`;
  }

  return firstBuyer || secondBuyer || "Unknown Buyer";
};

interface LotBuyersFromAudit {
  firstBuyer?: string;
  secondBuyer?: string;
}

const getLatestBuyerForLot = (
  lotId: string,
  fromDate: Date,
  toDate: Date | null,
  auditLogs: AuditLogInterface[]
): string | null => {
  const buyerChanges = auditLogs
    .filter((log) => {
      if (log.entityId !== lotId) return false;

      const logDate = new Date(log.createdAt);

      if (logDate < fromDate) return false;

      if (toDate && logDate > toDate) return false;

      const newData = log.changes?.new as
        | { lotBuyers?: LotBuyersFromAudit }
        | undefined;
      const oldData = log.changes?.old as
        | { lotBuyers?: LotBuyersFromAudit }
        | undefined;

      const hasNewBuyers =
        newData?.lotBuyers &&
        (newData.lotBuyers.firstBuyer || newData.lotBuyers.secondBuyer);
      const hasOldBuyers =
        oldData?.lotBuyers &&
        (oldData.lotBuyers.firstBuyer || oldData.lotBuyers.secondBuyer);

      return hasNewBuyers || hasOldBuyers;
    })
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

  if (buyerChanges.length > 0) {
    const latestLog = buyerChanges[0];
    const newData = latestLog.changes?.new as
      | { lotBuyers?: LotBuyersFromAudit }
      | undefined;

    if (
      newData?.lotBuyers &&
      (newData.lotBuyers.firstBuyer || newData.lotBuyers.secondBuyer)
    ) {
      return getBuyerName(
        newData.lotBuyers.firstBuyer,
        newData.lotBuyers.secondBuyer
      );
    }

    const oldData = latestLog.changes?.old as
      | { lotBuyers?: LotBuyersFromAudit }
      | undefined;

    if (
      oldData?.lotBuyers &&
      (oldData.lotBuyers.firstBuyer || oldData.lotBuyers.secondBuyer)
    ) {
      return getBuyerName(
        oldData.lotBuyers.firstBuyer,
        oldData.lotBuyers.secondBuyer
      );
    }
  }

  return null;
};

const getBuyerFromAuditLog = (
  log: AuditLogInterface,
  lot: LotForReportsInterface | null,
  isCancellation = false,
  skipLotFallback = false
): string => {
  const changes = log.changes;

  if (isCancellation) {
    const oldData = changes?.old as
      | { lotBuyers?: LotBuyersFromAudit; status?: string }
      | undefined;

    if (oldData?.lotBuyers) {
      return getBuyerName(
        oldData.lotBuyers.firstBuyer,
        oldData.lotBuyers.secondBuyer
      );
    }
  }

  const newData = changes?.new as
    | { lotBuyers?: LotBuyersFromAudit; status?: string }
    | undefined;

  if (newData?.lotBuyers) {
    return getBuyerName(
      newData.lotBuyers.firstBuyer,
      newData.lotBuyers.secondBuyer
    );
  }

  const oldData = changes?.old as
    | { lotBuyers?: LotBuyersFromAudit; status?: string }
    | undefined;

  if (oldData?.lotBuyers) {
    return getBuyerName(
      oldData.lotBuyers.firstBuyer,
      oldData.lotBuyers.secondBuyer
    );
  }

  if (!skipLotFallback && lot) {
    return getBuyerName(lot.firstBuyer, lot.secondBuyer);
  }

  return "Unknown Buyer";
};

interface StatusBadgeMapping {
  statusState: "cancelled" | "progressed";
  badge: string;
  badgeClass: string;
}

type StatusTransitionMap = Record<string, StatusBadgeMapping>;

const getStatusInfo = (
  lotId: string,
  statusDate: Date,
  currentStatus: LotStatus,
  auditLogs: AuditLogInterface[],
  currentLot: LotForReportsInterface | null,
  transitionMap: StatusTransitionMap,
  stillStatusInfo: {
    badge: string;
    badgeClass: string;
    statusState: "still-hold" | "still-under-contract" | "still-unconditional";
  }
): LotStatusInfo => {
  const laterChanges = auditLogs
    .filter((log) => {
      if (log.entityId !== lotId || !log.changes?.new) return false;

      const changeDate = new Date(log.createdAt);
      const statusChange = log.changes.new as { status?: string };

      return (
        changeDate > statusDate &&
        statusChange?.status &&
        statusChange.status !== currentStatus
      );
    })
    .sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );

  if (laterChanges.length > 0) {
    const changeLog = laterChanges[0];
    const changeDate = new Date(changeLog.createdAt);
    const nextStatus = (changeLog.changes.new as { status?: string })?.status;

    const latestBuyer = getLatestBuyerForLot(
      lotId,
      statusDate,
      changeDate,
      auditLogs
    );

    const oldData = changeLog.changes.old as
      | { lotBuyers?: LotBuyersFromAudit }
      | undefined;
    const buyerFromChangeLog = oldData?.lotBuyers
      ? getBuyerName(
          oldData.lotBuyers.firstBuyer,
          oldData.lotBuyers.secondBuyer
        )
      : undefined;

    const buyerFromCancellation = latestBuyer || buyerFromChangeLog;

    if (nextStatus && transitionMap[nextStatus]) {
      return {
        ...transitionMap[nextStatus],
        buyerFromCancellation,
      };
    }
  }

  const latestBuyerWhileInStatus = getLatestBuyerForLot(
    lotId,
    statusDate,
    null,
    auditLogs
  );

  if (currentLot && currentLot.status === currentStatus) {
    return {
      statusState: stillStatusInfo.statusState,
      badge: stillStatusInfo.badge,
      badgeClass: stillStatusInfo.badgeClass,
      buyerFromCancellation: latestBuyerWhileInStatus || undefined,
    };
  }

  return {
    statusState: stillStatusInfo.statusState,
    badge: stillStatusInfo.badge,
    badgeClass: stillStatusInfo.badgeClass,
    buyerFromCancellation: latestBuyerWhileInStatus || undefined,
  };
};

const getHoldStatus = (
  lotId: string,
  holdDate: Date,
  auditLogs: AuditLogInterface[],
  currentLot: LotForReportsInterface | null
): LotStatusInfo => {
  const transitionMap: StatusTransitionMap = {
    [LotStatus.AVAILABLE]: {
      statusState: "cancelled",
      badge: "Cancelled",
      badgeClass: "badge-outline gray",
    },
    [LotStatus.UNDER_CONTRACT]: {
      statusState: "progressed",
      badge: "Progressed to Contract",
      badgeClass: "badge-outline blue",
    },
    [LotStatus.UNCONDITIONAL]: {
      statusState: "progressed",
      badge: "Progressed to Contract",
      badgeClass: "badge-outline blue",
    },
  };

  return getStatusInfo(
    lotId,
    holdDate,
    LotStatus.HOLD,
    auditLogs,
    currentLot,
    transitionMap,
    {
      badge: "Still on hold",
      badgeClass: "badge-outline sky",
      statusState: "still-hold",
    }
  );
};

const getUnconditionalStatus = (
  lotId: string,
  unconditionalDate: Date,
  auditLogs: AuditLogInterface[],
  currentLot: LotForReportsInterface | null
): LotStatusInfo => {
  const transitionMap: StatusTransitionMap = {
    [LotStatus.SETTLED]: {
      statusState: "progressed",
      badge: "Progressed to Settled",
      badgeClass: "badge-outline pink",
    },
    [LotStatus.UNDER_CONTRACT]: {
      statusState: "progressed",
      badge: "Moved to Under Contract",
      badgeClass: "badge-outline blue",
    },
    [LotStatus.AVAILABLE]: {
      statusState: "cancelled",
      badge: "Contract terminated",
      badgeClass: "badge-outline gray",
    },
    [LotStatus.HOLD]: {
      statusState: "cancelled",
      badge: "Contract terminated",
      badgeClass: "badge-outline gray",
    },
    [LotStatus.NOT_RELEASED]: {
      statusState: "cancelled",
      badge: "Contract terminated",
      badgeClass: "badge-outline gray",
    },
  };

  return getStatusInfo(
    lotId,
    unconditionalDate,
    LotStatus.UNCONDITIONAL,
    auditLogs,
    currentLot,
    transitionMap,
    {
      badge: "Still Unconditional",
      badgeClass: "badge-outline purple",
      statusState: "still-unconditional",
    }
  );
};

const getUnderContractStatus = (
  lotId: string,
  contractDate: Date,
  auditLogs: AuditLogInterface[],
  currentLot: LotForReportsInterface | null
): LotStatusInfo => {
  const transitionMap: StatusTransitionMap = {
    [LotStatus.SETTLED]: {
      statusState: "progressed",
      badge: "Progressed to Settled",
      badgeClass: "badge-outline pink",
    },
    [LotStatus.UNCONDITIONAL]: {
      statusState: "progressed",
      badge: "Moved to Unconditional",
      badgeClass: "badge-outline purple",
    },
    [LotStatus.AVAILABLE]: {
      statusState: "cancelled",
      badge: "Contract terminated",
      badgeClass: "badge-outline gray",
    },
    [LotStatus.HOLD]: {
      statusState: "cancelled",
      badge: "Contract terminated",
      badgeClass: "badge-outline gray",
    },
    [LotStatus.NOT_RELEASED]: {
      statusState: "cancelled",
      badge: "Contract terminated",
      badgeClass: "badge-outline gray",
    },
  };

  return getStatusInfo(
    lotId,
    contractDate,
    LotStatus.UNDER_CONTRACT,
    auditLogs,
    currentLot,
    transitionMap,
    {
      badge: "Still Under Contract",
      badgeClass: "badge-outline blue",
      statusState: "still-under-contract",
    }
  );
};

const calculateRecentSalesActivity = (
  auditLogs: AuditLogInterface[],
  stages: StageInterface[],
  selectedStageId?: string
): SalesActivityResult => {
  const counts: ActivityCounts = {
    putOnHold: 0,
    contractsSigned: 0,
    cancellations: 0,
    unconditional: 0,
    settlements: 0,
  };

  const details: ActivityDetails = {
    settlements: [],
    contractsSigned: [],
    contractsTerminated: [],
    putOnHold: [],
    holdsProgressed: [],
    holdsCancelled: [],
    wentUnconditional: [],
  };

  const processedHolds = new Set<string>();
  const processedContracts = new Set<string>();

  const relevantLogs = auditLogs.filter((log) => {
    if (!selectedStageId || selectedStageId === "all") return true;

    const lot = getLotFromStages(stages, log.entityId);

    if (!lot) return false;

    const stage = stages.find((s) => s.lots.some((l) => l._id === lot._id));

    return stage?._id === selectedStageId;
  });

  const sortedLogs = [...relevantLogs].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  for (const log of sortedLogs) {
    const changes = log.changes;

    if (!changes) continue;

    const newStatus = (changes.new as { status?: string })?.status;
    const oldStatus = (changes.old as { status?: string })?.status;

    if (!newStatus) continue;

    const lot = getLotFromStages(stages, log.entityId);

    if (!lot) continue;

    const price = lot.price?.value || 0;
    const date = new Date(log.createdAt);

    const isCancellation =
      newStatus === LotStatus.AVAILABLE &&
      oldStatus &&
      [
        LotStatus.UNDER_CONTRACT,
        LotStatus.UNCONDITIONAL,
        LotStatus.HOLD,
      ].includes(oldStatus as LotStatus);

    const isContractTermination =
      oldStatus === LotStatus.UNDER_CONTRACT &&
      (newStatus === LotStatus.AVAILABLE || newStatus === LotStatus.HOLD);

    if (newStatus === LotStatus.SETTLED) {
      counts.settlements++;

      let buyer = getBuyerFromAuditLog(log, lot, false, true);

      if (buyer === "Unknown Buyer") {
        buyer =
          getLatestBuyerForLot(log.entityId, new Date(0), date, auditLogs) ||
          getBuyerFromAuditLog(log, lot, false);
      }

      details.settlements.push({
        lotTitle: lot.title,
        lotId: lot._id,
        date,
        price,
        buyer,
      });
    }

    if (
      newStatus === LotStatus.UNDER_CONTRACT &&
      !processedContracts.has(log._id)
    ) {
      processedContracts.add(log._id);
      counts.contractsSigned++;

      const contractStatusInfo = getUnderContractStatus(
        log.entityId,
        date,
        auditLogs,
        lot
      );
      const buyer =
        contractStatusInfo.buyerFromCancellation ||
        getBuyerFromAuditLog(log, lot, false, true);

      details.contractsSigned.push({
        lotTitle: lot.title,
        lotId: lot._id,
        date,
        price,
        buyer,
        badge: contractStatusInfo.badge,
        badgeClass: contractStatusInfo.badgeClass,
      });
    }

    if (
      newStatus === LotStatus.HOLD &&
      !processedHolds.has(`${log.entityId}-${date.getTime()}`)
    ) {
      processedHolds.add(`${log.entityId}-${date.getTime()}`);
      counts.putOnHold++;

      const holdStatusInfo = getHoldStatus(log.entityId, date, auditLogs, lot);

      const buyer =
        holdStatusInfo.buyerFromCancellation ||
        getBuyerFromAuditLog(log, lot, false, true);

      details.putOnHold.push({
        lotTitle: lot.title,
        lotId: lot._id,
        date,
        price,
        buyer,
        badge: holdStatusInfo.badge,
        badgeClass: holdStatusInfo.badgeClass,
      });

      if (holdStatusInfo.statusState === "progressed") {
        details.holdsProgressed.push({
          lotTitle: lot.title,
          lotId: lot._id,
          date,
          price,
          buyer,
          badge: holdStatusInfo.badge,
          badgeClass: holdStatusInfo.badgeClass,
        });
      } else if (holdStatusInfo.statusState === "cancelled") {
        details.holdsCancelled.push({
          lotTitle: lot.title,
          lotId: lot._id,
          date,
          price,
          buyer,
          badge: holdStatusInfo.badge,
          badgeClass: holdStatusInfo.badgeClass,
        });
      }
    }

    if (isContractTermination) {
      const buyer = getBuyerFromAuditLog(log, lot, true);

      counts.cancellations++;
      details.contractsTerminated.push({
        lotTitle: lot.title,
        lotId: lot._id,
        date,
        price,
        buyer,
        badge: "Contract terminated",
        badgeClass: "badge-outline gray",
      });
    }

    if (newStatus === LotStatus.UNCONDITIONAL) {
      counts.unconditional++;

      const unconditionalStatusInfo = getUnconditionalStatus(
        log.entityId,
        date,
        auditLogs,
        lot
      );
      const buyer =
        unconditionalStatusInfo.buyerFromCancellation ||
        getBuyerFromAuditLog(log, lot, false, true);

      details.wentUnconditional.push({
        lotTitle: lot.title,
        lotId: lot._id,
        date,
        price,
        buyer,
        badge: unconditionalStatusInfo.badge,
        badgeClass: unconditionalStatusInfo.badgeClass,
      });
    }
  }

  details.settlements.sort((a, b) => b.date.getTime() - a.date.getTime());
  details.contractsSigned.sort((a, b) => b.date.getTime() - a.date.getTime());
  details.contractsTerminated.sort(
    (a, b) => b.date.getTime() - a.date.getTime()
  );
  details.putOnHold.sort((a, b) => b.date.getTime() - a.date.getTime());
  details.holdsProgressed.sort((a, b) => b.date.getTime() - a.date.getTime());
  details.holdsCancelled.sort((a, b) => b.date.getTime() - a.date.getTime());
  details.wentUnconditional.sort((a, b) => b.date.getTime() - a.date.getTime());

  const totalSettlementsValue = details.settlements.reduce(
    (sum, item) => sum + item.price,
    0
  );

  const holdStatusCounts = {
    stillHold: details.putOnHold.filter((h) => h.badge === "Still on hold")
      .length,
    cancelled: details.putOnHold.filter((h) => h.badge === "Cancelled").length,
    progressed: details.putOnHold.filter(
      (h) => h.badge === "Progressed to Contract"
    ).length,
  };

  const holdNotes = [];

  if (holdStatusCounts.stillHold > 0)
    holdNotes.push(`${holdStatusCounts.stillHold} still on Hold`);

  if (holdStatusCounts.cancelled > 0)
    holdNotes.push(`${holdStatusCounts.cancelled} Cancelled`);

  if (holdStatusCounts.progressed > 0)
    holdNotes.push(`${holdStatusCounts.progressed} Progressed`);

  return {
    counts,
    details,
    notes: {
      putOnHold: holdNotes.join(", "),
      settlements:
        counts.settlements > 0
          ? `${formatPrice(totalSettlementsValue)} total value`
          : "",
    },
  };
};

export default calculateRecentSalesActivity;
